/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { GslbApiService, Layer4ApiService, Layer7ApiService } from '../../../http';
import { GslbRealServerComponent } from '../gslb';
import { L7RealServiceModel, TemplateModel } from '../../../models';
import { Layer4RealComponent } from '../l4Services';
import { Layer7RealComponent } from '../l7Services/servicesReal/layer7-real.component';
import { LogService, UtilsService } from '../../../../../../core/services';
import { TableMultiSelectModel } from '../../../../../../core/models/tableMultiSelect.model';
import { WizardControllerStateService, WizardService, WizardTemplateService } from '../../../services';
import { WizardTitleEnum } from '../../../enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-real-server-table',
    templateUrl: './real-server-table.component.html',
    styleUrls: ['./real-server-table.component.scss', '../../../../../../../assets/shared/scss/embedded.scss']
})

export class RealServerTableComponent extends BaseComponent implements OnInit {
    /**
     * Add / Edit container.
     */
    @ViewChild('addEditContainer', { read: ViewContainerRef, static: true })
    public addEditContainer: any;

    /**
     * Incoming templateType.
     */
    @Input() public templateType: 'layer4' | 'layer7' | 'gslb';

    /**
     * Incoming templateType.
     */
    @Input() public templateIndex: number = 0;

    /**
     * Inputs used when not routing to this page (e.g. from a wizard).
     * @param ripIds
     */
    @Input() set ripIds(ripIds: string[]) {
        this._ripIds = ripIds;
    }

    /**
     * Selected IP ID.
     */
    @Output() public ripIdsOut: EventEmitter<string[]> = new EventEmitter<string[]>();

    /**
     * Flag to determine if in add/edit mode.
     */
    public addEditMode: boolean = false;

    /**
     * Table rows
     */
    public tableRows: L7RealServiceModel[] = [];

    /**
     * Store of multiple selected rows in the table.
     */
    public selectedRows: any = [];

    /**
     * Store button text and action for select all action dots.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store if we are in edit mode.
     */
    public editMode: boolean = false;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        backends: []
    };

    /**
     * Store new real servers for submission.
     * @private
     */
    private newRealServers: L7RealServiceModel[] = [];

    /**
     * private variable for setters and getters.
     * @private
     */
    private _ripIds: string[] | any = [];

    /**
     * Modal Component Reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private cdr: ChangeDetectorRef,
        private gslbApiService: GslbApiService,
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardControllerStateService: WizardControllerStateService,
        private wizardService: WizardService,
        private wizardTemplateService: WizardTemplateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initLoading();
        this.setSelectAllActions();
    }

    get ripIds(): string[] {
        return this._ripIds;
    }

    /**
     * Triggered when a table row is selected.
     * @param selected
     */
    public onSelect(selected: any): void {
        const ripIds: string[] = selected.map(item => item.id);
        if (ripIds !== this.ripIds) {
            this.ripIdsOut.next(ripIds);
        }

        this.selectedRows = selected;
    }

    /**
     * Duplicate real server.
     * @param row
     */
    public duplicateRealServer(row: any): void {
        row.id = 'temp-' + Date.now() + Math.random();
        const serviceTemplate: TemplateModel = this.wizardService.selectedTemplate;
        const convertedRealServerSection = this.wizardTemplateService.convertServiceToTemplate(
            serviceTemplate, WizardTitleEnum.REALSERVERS, row
        );

        this.wizardControllerStateService.addServiceToControllerState(
            this.wizardService.controllerState, WizardTitleEnum.REALSERVERS, convertedRealServerSection
        );

        this.addEditRealServer(row);
    }

    /**
     * Add edit real server.
     * @param row
     */
    public addEditRealServer(row?: any): void {
        const realServers: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.REALSERVERS}`);
        let templateIndex: number = 0;
        if (realServers && !row) {
            templateIndex = realServers?.length && !row ? realServers.length : 0;
            this.addAnotherRealServerToControllerState(realServers);
        }

        const component: Type<any> = this.getComponent(this.templateType, 'component');
        this.addEditMode = true;
        this.wizardService.disableNavigation = true;
        const addEditComponent: any = this.addEditContainer.createComponent(component);
        addEditComponent.instance.isAddEdit = false;
        addEditComponent.instance.templateIndex = templateIndex;
        addEditComponent.instance.editId = row ? row.id : '';
        addEditComponent.instance.callBack = (rip: any) => {
            this.addNewRipToRows(rip);
        };

        this.componentReference = addEditComponent;
        this.cdr.detectChanges();
    }

    /**
     * Get real server component or service based on template.
     * @param templateType
     * @param get
     * @private
     */
    private getComponent(templateType: any, get: 'component' | 'service'): Type<any> | any {
        switch (templateType) {
            case 'layer4':
                return get === 'component' ? Layer4RealComponent : this.layer4ApiService;
            case 'layer7':
                return get === 'component' ? Layer7RealComponent : this.layer7ApiService;
            case 'gslb':
                return get === 'component' ? GslbRealServerComponent : this.gslbApiService;
        }
    }

    /**
     * Delete newly created real server.
     * @param row
     */
    public deleteRealServer(row: any): void {
        const backends: any = this.utilsService.getObjectValue(this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}`);
        const backend: any = backends[this.templateIndex];

        if (backend?.id?.value) {
            this.wizardControllerStateService.deleteServiceItemInControllerStateById(
                this.wizardService.controllerState,
                WizardTitleEnum.BACKENDS,
                backend.id,
                'ripIds',
                row.id
            );
        }

        this.wizardControllerStateService.deleteServiceFromControllerStateByServiceId(
            this.wizardService.controllerState,
            WizardTitleEnum.REALSERVERS,
            row.id
        );

        this.initLoading();
    }

    /**
     * Add another real server to controller state.
     * @param realServers
     * @private
     */
    private addAnotherRealServerToControllerState(realServers: any): void {
        const newRealTemplate = this.utilsService.getObjectValue(
            this.wizardService.selectedTemplate, `hiddenSections.${WizardTitleEnum.REALSERVERS}.0`
        );

        if (!newRealTemplate) {
            this.logService.error(
                'addAnotherRealServerToControllerState:RealServerTableComponent',
                'Unable to find a real server template'
            );
        } else {
            newRealTemplate['id'] = { value: 'temp-' + Date.now() + Math.random() };
            realServers.push(newRealTemplate);
        }
    }

    /**
     * Add new real server to table rows.
     * @param rip
     * @private
     */
    private addNewRipToRows(rip?: L7RealServiceModel): void {
        if (rip) {
            const ripId: string = rip.id === '' ? 'temp-' + Date.now() + Math.random() : rip.id;
            this.ripIds.push(ripId);
        }

        this.addEditMode = false;
        this.wizardService.disableNavigation = false;
        this.componentReference.destroy();

        this.initLoading();
    }

    /**
     * Set the select all button.
     * @private
     */
    private setSelectAllActions(): void {
        this.multiSelectActions = [
            {
                buttonText: 'needs to be removed',
                buttonAction: () => {
                },
                buttonDisabled: true
            }
        ];
    }

    /**
     * Get real servers and load table.
     * @private
     */
    private initLoading(): void {
        const apiService: any = this.getComponent(this.templateType, 'service');
        // this.templateType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
        apiService.getRealServers()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (realServers: L7RealServiceModel[]): void => {
                    const controllerStateReals: any[] = this.getControllerStateRealServers();
                    const reals: L7RealServiceModel[] = controllerStateReals?.length
                        ? realServers.concat(controllerStateReals)
                        : realServers;
                    const tableRows: any[] = this.populateTableRows(reals);
                    const selected: any[] = tableRows.filter((item: any): boolean => this.ripIds.includes(item.id));
                    this.onSelect(selected);

                    this.tableRows = tableRows;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServicesRealComponent:initLoading', error.message);
                }
            });
    }

    /**
     * Get controller state realservers
     * @private
     */
    private getControllerStateRealServers(): any[] {
        const controllerStateReals: any[] = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.REALSERVERS}`
        );

        if (!controllerStateReals?.length) {
            this.logService.debug('getControllerStateRealServers', 'No real servers found');

            return [];
        }

        const convertedServices: any[] = [];
        for (const real of controllerStateReals) {
            convertedServices.push(this.wizardTemplateService.convertTemplateToService(real));
        }

        return convertedServices;
    }

    /**
     * Populate table rows.
     * @param realServers
     * @private
     */
    private populateTableRows(realServers: any[]): any[] {
        const realServerConcat: any[] = realServers.concat(this.newRealServers);
        const tableRows: any[] = [];
        for (const realServer of realServerConcat) {
            delete realServer['statsId'];

            const row: any = {
                id: realServer.id,
                name: realServer.name,
                ip: realServer.ip,
                port: realServer.port,
                usage: realServer.usage,
                weight: realServer.weight,
                existingRip: this.utilsService.isServiceId(realServer.id),
            };

            if (this.templateType === 'layer4') {
                row['minimumConnections'] = realServer.minimumConnections;
                row['maximumConnections'] = realServer.maximumConnections;
            }

            if (this.templateType === 'layer7') {
                row['backup'] = realServer.backup;
                row['disabled'] = realServer.disabled;
                row['encryptToBackend'] = realServer.encryptToBackend;
                row['maxconns'] = realServer.maxconns;
                row['minconns'] = realServer.minconns;
            }

            tableRows.push(row);
        }

        return tableRows;
    }
}
